import { useLayoutEffect, useRef, useState, useEffect } from "react";
import { useDataContext } from "../../Context/DataContext";
import gsap from "gsap";
import { Draggable } from "gsap/dist/Draggable";

if (typeof window !== "undefined") {
    gsap.registerPlugin(Draggable);
}

export const Other = () => {
    const { others } = useDataContext();
    const sectionRef = useRef(null);
    const marqueeRef = useRef(null);
    const titleRef = useRef(null);
    const marqueeTl = useRef(null);

    const [isPaused, setIsPaused] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [prevIndex, setPrevIndex] = useState(0); // Track direction
    const [isMobile, setIsMobile] = useState(false);

    const DOT_SIZE = 6;
    const GAP = 4;
    const STEP = DOT_SIZE + (GAP * 2);

    useEffect(() => {
        const timer = setTimeout(() => setPrevIndex(currentIndex), 500);
        return () => clearTimeout(timer);
    }, [currentIndex]);

    const scrollToCenter = (index) => {
        const container = marqueeRef.current;
        const items = container.querySelectorAll(".other-item");
        if (!items[index]) return;

        const item = items[index];
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        const screenCenter = window.innerWidth / 2;
        const targetX = screenCenter - itemCenter;

        gsap.to(container, {
            x: targetX,
            duration: 0.7,
            ease: "expo.out",
        });
        setCurrentIndex(index);
    };

    const slide = (direction) => {
        let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
        newIndex = Math.max(0, Math.min(newIndex, others.length - 1));
        scrollToCenter(newIndex);
    };

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const mm = gsap.matchMedia();

            mm.add("(min-width: 768px)", () => {
                setIsMobile(false);
                const container = marqueeRef.current;

                // 1. Reset X for clean start
                gsap.set(container, { x: 0 });

                // 2. Clear old clones to prevent duplicates
                const oldClones = container.parentElement.querySelectorAll(".clone");
                oldClones.forEach(el => el.remove());

                const totalWidth = container.scrollWidth;
                const clone = container.cloneNode(true);
                clone.classList.add("clone");
                clone.style.position = "absolute";
                clone.style.left = `${totalWidth}px`;
                clone.style.top = "0";
                container.parentElement.appendChild(clone);

                // 3. Re-initiate seamless loop
                marqueeTl.current = gsap.to([container, clone], {
                    x: `-=${totalWidth}`,
                    duration: 30,
                    ease: "none",
                    repeat: -1,
                    modifiers: { x: gsap.utils.unitize((x) => parseFloat(x) % totalWidth) },
                });

                const parent = container.parentElement;
                parent.onmouseenter = () => { marqueeTl.current?.pause(); setIsPaused(true); };
                parent.onmouseleave = () => { marqueeTl.current?.play(); setIsPaused(false); };

                return () => { if (clone) clone.remove(); };
            });

            mm.add("(max-width: 768px)", () => {
                setIsMobile(true);
                if (marqueeTl.current) marqueeTl.current.kill();

                // Center the first item on mobile load
                setTimeout(() => scrollToCenter(0), 10);

                Draggable.create(marqueeRef.current, {
                    type: "x",
                    edgeResistance: 0.8,
                    inertia: true,
                    onDragEnd: function () {
                        const items = marqueeRef.current.querySelectorAll(".other-item");
                        let closestIndex = 0;
                        let minDistance = Number.MAX_VALUE;

                        items.forEach((item, i) => {
                            const itemCenter = item.offsetLeft + (item.offsetWidth / 2) + this.x;
                            const distance = Math.abs(window.innerWidth / 2 - itemCenter);
                            if (distance < minDistance) {
                                minDistance = distance;
                                closestIndex = i;
                            }
                        });
                        scrollToCenter(closestIndex);
                    }
                });
            });

            gsap.from(titleRef.current, {
                scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
                y: 50, opacity: 0, duration: 0.8,
            });
        }, sectionRef);

        return () => ctx.revert();
    }, [others.length]);

    // Calculate moveX logic for the dots track
    const getTrackTransform = () => {
        const total = others.length;
        let moveX = 0;

        if (total <= 5) {
            moveX = -(total * STEP / 2);
        } else {
            if (currentIndex <= 2) {
                moveX = -(STEP * (2 + currentIndex * 0.5));
            } else if (currentIndex >= total - 3) {
                const distFromEnd = (total - 1) - currentIndex;
                moveX = -((total - 2 - distFromEnd * 0.5) * STEP);
            } else {
                moveX = -(currentIndex * STEP + (STEP / 2));
            }
        }
        return `translateX(${moveX}px)`;
    };

    // Determine direction class
    const directionClass = currentIndex > prevIndex ? "moving-next" : currentIndex < prevIndex ? "moving-prev" : "";

    return (
        <div ref={sectionRef} className="bg-gradient-to-b from-b-col to-[#72daf726] overflow-hidden pb-16 pt-10">
            <div className="md:px-20 xl:px-32">
                <div className="container mx-auto px-9">
                    <div className="flex relative items-center justify-between pb-12">
                        <p ref={titleRef} className="font-pockota font-bold text-2xl sm:text-3xl md:text-4xl dark:text-white">
                            Others
                        </p>

                        {!isMobile ? (
                            <label className="size-10 cursor-pointer flex flex-col items-center justify-center absolute right-0 bg-white/70 dark:bg-black/50 backdrop-blur-md rounded-full hover:scale-105 transition-transform">
                                <input
                                    className="hidden peer"
                                    type="checkbox"
                                    onClick={() => {
                                        if (isPaused) marqueeTl.current.play(); else marqueeTl.current.pause();
                                        setIsPaused(!isPaused);
                                    }}
                                    checked={isPaused}
                                    readOnly
                                />
                                <div className="w-[50%] h-[2px] bg-white rounded-sm transition-all duration-300 origin-center rotate-90 -translate-x-[0.3rem] translate-y-[0.1rem] peer-checked:translate-y-[0.1rem]" />
                                <div className="w-[50%] h-[2px] bg-white rounded-md transition-all duration-300 origin-center rotate-90 translate-x-[0.3rem] -translate-y-[0.05rem] peer-checked:rotate-[-30deg] peer-checked:translate-y-[0.22rem] peer-checked:translate-x-[0.15rem]" />
                                <div className="w-[50%] h-[2px] bg-white rounded-md transition-all duration-300 origin-center rotate-90 translate-x-[0.3rem] -translate-y-[0.16rem] peer-checked:rotate-[30deg] peer-checked:translate-y-[-0.4rem] peer-checked:translate-x-[0.15rem]" />
                            </label>
                        ) : (
                            <div className="flex gap-4">
                                <button onClick={() => slide("prev")} className="size-10 flex items-center justify-center bg-white/70 dark:bg-black/50 text-black dark:text-white backdrop-blur-lg rounded-full border border-white/10 shadow-2xl disabled:opacity-10 active:scale-90 transition-all" disabled={currentIndex === 0}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                                </button>
                                <button onClick={() => slide("next")} className="size-10 flex items-center justify-center bg-white/70 dark:bg-black/50 text-black dark:text-white backdrop-blur-lg rounded-full border border-white/10 shadow-2xl disabled:opacity-10 active:scale-90 transition-all" disabled={currentIndex === others.length - 1}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="relative">
                <div
                    ref={marqueeRef}
                    className={`flex items-center whitespace-nowrap will-change-transform 
                        ${isMobile ? 'gap-8 px-[20vw]' : 'gap-10 px-10'}`} // Conditional spacing
                >
                    {others.map((item, index) => (
                        <div
                            key={index}
                            className={`other-item flex-shrink-0 transition-all duration-700 ease-out
                                ${isMobile && currentIndex === index ? 'scale-105 opacity-100' : ''}
                                ${isMobile && currentIndex !== index ? 'scale-90 opacity-40 blur-[1px]' : ''}
                                ${!isMobile ? 'opacity-100 blur-0 scale-100' : ''}`}
                        >
                            <a className="block w-fit mx-auto group" href={item.link} target="_blank" rel="noreferrer">
                                <div className="relative w-[280px] sm:w-[320px] md:w-[400px] overflow-hidden rounded-3xl shadow-2xl border-4 border-white/5">
                                    <img
                                        className={`w-full h-full object-cover transition-transform duration-1000 ease-out 
                                            ${isMobile && currentIndex === index ? 'scale-110' : 'scale-100'} 
                                            ${!isMobile ? 'group-hover:scale-110' : ''}`}
                                        src={item.img}
                                        alt={item.name}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />
                                </div>
                            </a>
                            <h3 className={`pt-8 text-center dark:text-white transition-all duration-500
                                ${isMobile ? 'text-xl font-medium tracking-widest' : 'text-2xl opacity-80 font-light'}`}>
                                {item.name}
                            </h3>
                        </div>
                    ))}
                </div>
            </div>

            {isMobile && (
                <div className="pagination-window relative h-6 flex items-center justify-center bg-black/60 backdrop-blur-xl rounded-full border border-white/10 overflow-hidden mx-auto transition-all mt-10"
                    style={{
                        width: 
                        `${(others.filter((_, i) => {
                            const diff = Math.abs(i - currentIndex);
                            return others.length <= 5 || diff <= 3;
                        }).length) * STEP + 24}px`
                    }}>

                    <div
                        className={`dots-track flex items-center absolute left-1/2 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${directionClass}`}
                        style={{ transform: getTrackTransform() }}
                    >
                        {others.map((_, i) => {
                            const diff = Math.abs(i - currentIndex);

                            let scale = 0;
                            if (others.length <= 5) {
                                scale = 1;
                            } else {
                                if (diff <= 2) scale = 1;
                                else if (diff === 3) scale = 0.5;
                                else scale = 0;
                            }

                            return (
                                <div
                                    key={i}
                                    onClick={() => scrollToCenter(i)}
                                    className={`
                            ${diff === 0 ? "dot-active" : ""} 
                            shrink-0 rounded-full cursor-pointer transition-all duration-500
                            bg-white/20
                        `}
                                    style={{
                                        width: `${DOT_SIZE}px`,
                                        height: `${DOT_SIZE}px`,
                                        margin: `0 ${GAP}px`,
                                        transform: `scale(${scale})`,
                                        opacity: scale,
                                        visibility: scale === 0 ? 'hidden' : 'visible'
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
